/**
 * Раздел с чужим интерфейсом: CRM, сервис WhatsApp, свободный браузер.
 *
 * ── Почему не iframe ────────────────────────────────────────────────────────
 *
 * Так встроить чужой рабочий интерфейс нельзя. Практически любая внутренняя
 * система отдаёт `X-Frame-Options: DENY` или `frame-ancestors 'none'`, и
 * браузер откажет молча — пустой белый прямоугольник без единого сообщения.
 * Даже там, где заголовков нет, во фрейме ломаются cookie (`SameSite`),
 * переходы, скачивание файлов и вход по SSO.
 *
 * Поэтому раздел открывается НАТИВНОЙ вкладкой оболочки — отдельным
 * WebContentsView со своей сессией и политиками. Для встроенного сайта это
 * полноценный браузер, а не рамка внутри страницы.
 *
 * ── Где лежит содержимое ────────────────────────────────────────────────────
 *
 * Нативная вкладка кладётся ПОВЕРХ страницы, а не внутрь неё: у неё нет места
 * в DOM. Поэтому компонент рисует пустой прямоугольник-местозаполнитель и
 * сообщает оболочке его координаты. Он же следит за изменением размера окна.
 *
 * ── В браузере ──────────────────────────────────────────────────────────────
 *
 * Оболочки нет — нативную вкладку открыть нечем. Показываем прямое объяснение
 * и кнопку «открыть в новом окне». Молча показать пустоту было бы худшим
 * исходом: раздел выглядел бы сломанным.
 */

import { useEffect, useRef, useState } from "react";
import type { SectionDto } from "../lib/api.js";
import {
  desktop,
  isDesktop,
  openExternal,
  type DesktopTabInfo,
} from "../lib/desktop.js";
import { resolveAddress } from "../lib/address.js";
import { useSession } from "../state/session.tsx";
import { MOBILE_QUERY } from "../lib/useMedia.ts";
import {
  AlertIcon,
  ChevronIcon,
  GlobeIcon,
  PinIcon,
  PinOffIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  XIcon,
} from "../components/icons.tsx";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Ключ, под которым запоминается открытая панель встроенного раздела. */
const TOOLBAR_KEY = "tw.section.toolbar";

interface SectionViewProps {
  section: SectionDto;
  /**
   * Адрес, открытый по ссылке из другого раздела.
   *
   * Заменяет собственный адрес раздела на этот показ и НЕ сохраняется: раздел
   * настроен администратором, и переход по ссылке из переписки не должен
   * менять его настройку.
   */
  overrideUrl?: string | null;
}

export function SectionView({ section, overrideUrl }: SectionViewProps) {
  const holder = useRef<HTMLDivElement | null>(null);
  const [tabs, setTabs] = useState<DesktopTabInfo[]>([]);
  const [address, setAddress] = useState(overrideUrl ?? section.url ?? "");
  /**
   * Правят ли адресную строку прямо сейчас.
   *
   * Строка следит за открытой страницей, но перебивать набор на полуслове
   * нельзя: человек печатает адрес, страница догружается и заменяет ему текст.
   */
  const [editing, setEditing] = useState(false);

  const bridge = desktop();
  const browsable = section.kind === "BROWSER";

  /**
   * Открыта ли панель встроенного раздела.
   *
   * Запоминаем между запусками: панель нужна изредка, и заново открывать её
   * при каждом переходе между разделами человек не подписывался. Ключ один на
   * все встроенные разделы — держать выбор отдельно для CRM и WhatsApp значило
   * бы, что одна и та же кнопка ведёт себя по-разному без всякой причины.
   *
   * Чтение защищено: в приватном окне и при запрете хранилища обращение к
   * localStorage выбрасывает, и раздел не должен из-за этого не открыться.
   */
  const [barOpen, setBarOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(TOOLBAR_KEY) === "open";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(TOOLBAR_KEY, barOpen ? "open" : "closed");
    } catch {
      /* хранилище недоступно — просто не запомним выбор */
    }
  }, [barOpen]);

  const { api } = useSession();

  /*
   * Пара для входа в рабочую систему уходит в оболочку.
   *
   * ── Зачем ───────────────────────────────────────────────────────────────
   *
   * Рабочие системы выбрасывают из сессии по своему усмотрению: по сроку, при
   * смене адреса, при входе с другого устройства. Раньше это означало, что
   * сотрудник посреди дня упирается в форму входа, идёт искать свой пароль и
   * до тех пор не работает.
   *
   * Оболочка держит пару в памяти и подставляет её КАЖДЫЙ раз, когда система
   * показала форму входа, — не только при первом открытии раздела.
   *
   * ── Почему запрос идёт здесь ─────────────────────────────────────────────
   *
   * Пароль хранится зашифрованным, расшифровать его может только сервер, и
   * выдача пишется в журнал. Поэтому запрашиваем ровно при показе раздела, а
   * не заранее для всех разделов сразу: незачем расшифровывать то, чем сегодня
   * не пользуются.
   *
   * Отсутствие пары — обычное состояние, а не ошибка: сотрудник мог её ещё не
   * завести, и вход руками остаётся рабочим путём.
   */
  const sectionId = section.id;
  const sectionUrl = section.url;
  const { loginSelector, passwordSelector, submitSelector } = section;

  useEffect(() => {
    const url = sectionUrl;

    // Селекторы не требуются: форму оболочка находит сама, а они лишь
    // уточняют её работу там, где автоопределение не справилось.
    if (!bridge?.setSectionLogin || !url) return;

    let cancelled = false;

    void api
      .revealSectionCredential(sectionId)
      .then((credential) => {
        if (cancelled) return;
        bridge.setSectionLogin?.({
          url,
          login: credential.login,
          password: credential.password,
          loginSelector,
          passwordSelector,
          submitSelector,
        });
      })
      .catch(() => {
        /* пары нет — входим руками */
      });

    return () => {
      cancelled = true;
    };
  }, [
    api,
    bridge,
    sectionId,
    sectionUrl,
    loginSelector,
    passwordSelector,
    submitSelector,
  ]);

  /*
   * Показываем вкладку раздела.
   *
   * Вызов идемпотентен: оболочка найдёт уже открытую вкладку по адресу либо
   * заведёт новую. Метки «уже открывали» здесь быть не должно — именно она
   * и ломала показ: StrictMode прогоняет эффекты дважды, очистка прятала
   * вкладки, а повторный проход по метке уже ничего не показывал.
   *
   * Переключение между CRM и WhatsApp страницу не перезагружает: вкладка
   * остаётся живой, меняется только видимость.
   */
  const shownUrl = overrideUrl ?? section.url;

  useEffect(() => {
    if (!bridge || !shownUrl) return;

    /*
     * Вкладка встроенного раздела принадлежит ему лично.
     *
     * Без этого признака вкладки CRM и WhatsApp попадали в общую полосу
     * «Браузера»: сотрудник открывал «Браузер», а видел там чужие рабочие
     * страницы — да ещё и мог закрыть их крестиком, после чего раздел
     * приходилось открывать заново.
     *
     * У самого «Браузера» признака нет: его стартовая страница и всё, что
     * сотрудник открыл рядом, — это и есть содержимое его полосы.
     */
    bridge.showSection(
      shownUrl,
      section.kind === "EMBEDDED" ? shownUrl : undefined,
    );
    return () => bridge.hideTabs();
  }, [bridge, shownUrl, section.kind]);

  // Адресная строка следует за пришедшей ссылкой: иначе она показывала бы
  // адрес раздела, а на экране была бы совсем другая страница.
  useEffect(() => {
    if (overrideUrl) setAddress(overrideUrl);
  }, [overrideUrl]);

  useEffect(() => {
    if (!bridge) return;
    return bridge.onTabs(setTabs);
  }, [bridge]);

  /*
   * Слежение за областью.
   *
   * ResizeObserver, а не обработчик resize окна: область меняется и без
   * изменения размера окна — при сворачивании боковой колонки, появлении
   * баннера, смене раздела. Обработчик window.resize такие случаи пропускает,
   * и вкладка остаётся сдвинутой.
   */
  useEffect(() => {
    const node = holder.current;
    if (!bridge || !node) return;

    const report = (): void => {
      const r = node.getBoundingClientRect();
      bridge.setTabBounds({
        x: r.left,
        y: r.top,
        width: r.width,
        height: r.height,
      });
    };

    report();
    const observer = new ResizeObserver(report);
    observer.observe(node);
    window.addEventListener("scroll", report, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", report, true);
    };
  }, [bridge, section.id]);

  /*
   * Активная вкладка.
   *
   * Раньше «активной» считалась первая непустая — при одной вкладке это
   * совпадало с истиной, при нескольких кнопки «назад» и «вперёд» относились
   * не к той странице, на которую смотрит человек. Теперь оболочка сообщает
   * это прямо; выбор первой оставлен запасным путём для старой версии
   * приложения, которая поля active ещё не присылает.
   */
  const active = tabs.find((t) => t.active) ?? tabs[0] ?? null;

  /*
   * Вкладки, которым место в полосе «Браузера».
   *
   * Личные вкладки встроенных разделов исключаются: они принадлежат CRM и
   * WhatsApp, живут своей жизнью и закрывать их отсюда нельзя.
   *
   * Старая оболочка признака не присылает, и тогда список остаётся прежним —
   * это хуже нового поведения, но лучше пустой полосы.
   */
  const ownTabs = tabs.filter((tab) => !tab.exclusiveTo);

  /*
   * Адресная строка следует за открытой страницей.
   *
   * Раньше она показывала адрес, с которого раздел начинали, и после перехода
   * по ссылкам внутри страницы врала: на экране одно, в строке другое.
   * Скопировать адрес текущей страницы было неоткуда.
   */
  useEffect(() => {
    if (editing) return;
    const url = active?.url;
    if (url && url !== "about:blank") setAddress(url);
  }, [active?.url, editing]);

  const go = (): void => {
    const value = address.trim();
    if (!value || !bridge) return;
    const url = resolveAddress(value);

    /*
     * Ввод адреса ведёт ТЕКУЩУЮ вкладку, а не открывает новую.
     *
     * Здесь стоял openTab, и адресная строка работала как «открыть ещё одну»:
     * каждый переход плодил вкладку, полоса за полчаса работы забивалась
     * десятком копий одного сайта, а вернуться назад было нельзя — история
     * оставалась в прежней вкладке.
     */
    if (active) bridge.navigateTab(url);
    else bridge.openTab(url);

    setEditing(false);
  };

  if (!isDesktop()) {
    // Телефон (PWA): раздел-сайт открывается в браузере телефона. Автовхода
    // там нет — пароли разделов на телефон не уходят (docs/PWA.md).
    const phone = window.matchMedia(MOBILE_QUERY).matches;
    return (
      <div
        className={cn(
          "flex h-full items-center justify-center p-8",
          !phone && "rounded-2xl border border-border bg-card shadow-md",
        )}
      >
        <div className="flex max-w-md flex-col items-center gap-3 text-center">
          <span
            aria-hidden
            className="grid size-12 place-items-center rounded-2xl bg-secondary text-brand"
          >
            <AlertIcon className="size-6" />
          </span>
          <p className="text-lg font-semibold tracking-[-0.019em] text-heading">
            {section.title}
          </p>
          {phone ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              На телефоне раздел открывается в браузере. Войдите своим логином и
              паролем от «{section.title}» — автоматический вход работает только
              в приложении BUCOR на компьютере.
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-muted-foreground">
              Раздел работает только в приложении. В браузере его встроить нельзя:
              рабочие системы запрещают показ внутри рамки, и вместо интерфейса
              получилась бы пустая область.
            </p>
          )}
          {section.url && (
            <Button
              variant="brand"
              size={phone ? "lg" : "default"}
              onClick={() => openExternal(section.url as string)}
            >
              {phone ? `Открыть ${section.title}` : "Открыть в новом окне"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col",
        // У встроенного раздела зазор меньше: панель и так свёрнута, и
        // восемь пикселей пустоты над ней — заметная часть того, что мы
        // только что отвоевали у неё же.
        browsable ? "gap-2" : "gap-1",
      )}
    >
      {/*
        Полоса вкладок и панель адреса — единая поверхность.

        Раньше это были две отдельные карточки с зазором между ними, и активная
        вкладка висела над панелью, ни с чем не связанная: глазу не за что было
        зацепиться, чтобы понять, какая страница показана. Теперь активная
        вкладка того же цвета, что и панель под ней, и переходит в неё без
        границы — как в любом браузере.
      */}
      {browsable && (
        <div className="flex-none overflow-hidden rounded-2xl border border-border bg-card shadow-md">
          {ownTabs.length > 0 && (
            <div className="flex items-end gap-1 overflow-x-auto bg-secondary/60 px-2 pt-2">
              {ownTabs.map((tab) => (
                <div
                  key={tab.id}
                  className={cn(
                    "group relative flex h-9 w-[200px] flex-none items-center gap-2 rounded-t-lg px-2.5 text-[12px] transition-colors",
                    // Активная вкладка сливается с панелью под ней и слегка
                    // приподнята тенью: без этого при одинаковом фоне её
                    // приходится искать по насыщенности текста.
                    tab.active
                      ? "bg-card font-medium text-foreground shadow-[0_-1px_2px_rgba(0,0,0,0.04)]"
                      : "text-muted-foreground hover:bg-card/60",
                  )}
                  title={tab.title || tab.url}
                  /* Средняя кнопка мыши закрывает вкладку — привычно по любому
                     браузеру и не требует попадания в мелкий крестик. */
                  onAuxClick={(e) => {
                    if (e.button === 1 && !tab.pinned) {
                      e.preventDefault();
                      bridge?.closeTab(tab.id);
                    }
                  }}
                >
                  {/*
                    Кнопка переключения занимает всю вкладку, а не только
                    подпись: раньше клик по отступам не делал ничего, и
                    попадать приходилось ровно в текст.
                  */}
                  <button
                    type="button"
                    onClick={() => bridge?.activateTab(tab.id)}
                    aria-label={`Вкладка ${tab.title || tab.url}`}
                    className="absolute inset-0 rounded-t-lg border-transparent bg-transparent p-0"
                  />

                  <span
                    aria-hidden
                    className="pointer-events-none relative flex-none"
                  >
                    {tab.loading ? (
                      <RefreshIcon className="size-4 animate-spin text-muted-foreground" />
                    ) : tab.favicon ? (
                      <img
                        src={tab.favicon}
                        alt=""
                        className="size-4 rounded object-contain"
                      />
                    ) : (
                      <GlobeIcon className="size-4 text-muted-foreground" />
                    )}
                  </span>

                  {/*
                    Признак закрепления — канцелярская кнопка рядом с названием.

                    Раньше закреплённая вкладка сжималась до одного значка. Так
                    делает Chrome, но там вкладок десятки и место дорого; здесь
                    их единицы, а сжатие отнимало у закреплённой — то есть у
                    самой нужной — единственное, по чему её узнают: название.
                  */}
                  {tab.pinned && (
                    <PinIcon
                      aria-hidden
                      className="pointer-events-none relative size-3 flex-none text-brand"
                    />
                  )}

                  <span className="pointer-events-none relative min-w-0 flex-1 truncate">
                    {tab.title || "Новая вкладка"}
                  </span>

                  {/*
                    Закрепление и закрытие.

                    Появляются при наведении и при переходе с клавиатуры: до
                    этого кнопка закрепления была прозрачной, но оставалась в
                    порядке обхода — с клавиатуры фокус уходил на невидимое.
                  */}
                  <span
                    className={cn(
                      /*
                       * Скрыто — значит и не нажимается.
                       *
                       * Одной прозрачности мало: opacity-0 не убирает элемент
                       * из-под курсора, и клик по пустому месту справа во
                       * вкладке попадал бы в невидимый крестик. Поэтому вместе
                       * с прозрачностью снимаем и приём нажатий.
                       */
                      "relative flex flex-none items-center gap-0.5 opacity-0 transition-opacity",
                      "pointer-events-none focus-within:pointer-events-auto focus-within:opacity-100",
                      "group-hover:pointer-events-auto group-hover:opacity-100",
                      tab.active && "pointer-events-auto opacity-100",
                    )}
                  >
                    <button
                      type="button"
                      aria-label={
                        tab.pinned ? "Открепить вкладку" : "Закрепить вкладку"
                      }
                      title={tab.pinned ? "Открепить" : "Закрепить"}
                      onClick={() => bridge?.pinTab(tab.id, !tab.pinned)}
                      className="grid size-5 place-items-center rounded-md border-transparent bg-transparent p-0 text-muted-foreground transition-colors hover:bg-secondary hover:text-brand"
                    >
                      {tab.pinned ? (
                        <PinOffIcon className="size-3.5" />
                      ) : (
                        <PinIcon className="size-3.5" />
                      )}
                    </button>

                    {/* У закреплённой крестика нет: случайно закрыть страницу,
                        на которую возвращаются весь день, обиднее, чем
                        открепить её. */}
                    {!tab.pinned && (
                      <button
                        type="button"
                        aria-label="Закрыть вкладку"
                        title="Закрыть"
                        onClick={() => bridge?.closeTab(tab.id)}
                        className="grid size-5 place-items-center rounded-md border-transparent bg-transparent p-0 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive"
                      >
                        <XIcon className="size-3.5" />
                      </button>
                    )}
                  </span>
                </div>
              ))}

              <button
                type="button"
                aria-label="Новая вкладка"
                title="Новая вкладка"
                onClick={() => bridge?.openTab(section.url ?? "about:blank")}
                className="mb-1 grid size-8 flex-none place-items-center rounded-lg border-transparent bg-transparent text-muted-foreground transition-colors hover:bg-card hover:text-brand"
              >
                <PlusIcon className="size-4" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-1 px-2 py-2">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={!active?.canGoBack}
              onClick={() => bridge?.goBack()}
              aria-label="Назад"
              title="Назад"
              className="flex-none text-muted-foreground"
            >
              <ChevronIcon className="size-4 rotate-90" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={!active?.canGoForward}
              onClick={() => bridge?.goForward()}
              aria-label="Вперёд"
              title="Вперёд"
              className="flex-none text-muted-foreground"
            >
              <ChevronIcon className="size-4 -rotate-90" />
            </Button>

            {/* Обновление было доступно только через мост, но не кнопкой:
                единственным способом перезагрузить страницу оставалось ввести
                её адрес заново. */}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => bridge?.reloadTab()}
              aria-label={active?.loading ? "Идёт загрузка" : "Обновить страницу"}
              title="Обновить"
              className="mr-1 flex-none text-muted-foreground"
            >
              <RefreshIcon
                className={cn("size-4", active?.loading && "animate-spin")}
              />
            </Button>

            <form
              className="relative flex min-w-0 flex-1 items-center"
              onSubmit={(e) => {
                e.preventDefault();
                go();
              }}
            >
              <SearchIcon className="pointer-events-none absolute left-3 size-4 text-muted-foreground" />
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onFocus={(e) => {
                  setEditing(true);
                  // Выделяем адрес целиком: править его посимвольно почти
                  // никогда не нужно, а вводить новый — постоянно.
                  e.currentTarget.select();
                }}
                onBlur={() => setEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setEditing(false);
                    e.currentTarget.blur();
                  }
                }}
                placeholder="Адрес страницы"
                spellCheck={false}
                autoComplete="off"
                aria-label="Адрес страницы"
                className="h-9 w-full rounded-full border border-border bg-background pl-9 pr-3 text-sm outline-none transition-colors focus:border-ring"
              />
            </form>

            <Button size="sm" className="ml-1 flex-none" onClick={go}>
              Открыть
            </Button>
          </div>
        </div>
      )}

      {/*
        Управление встроенным разделом.

        Его не было вовсе: и полоса вкладок, и панель с кнопками показывались
        только «Браузеру». У CRM и WhatsApp не было ни одной кнопки — а
        встроенная страница вполне может показать ошибку, из которой выходят
        именно перезагрузкой. Живой пример: Laravel отдаёт «419 Page Expired»,
        когда истёк CSRF-токен, и без обновления страницы из этого состояния не
        выбраться. Единственным способом оставался перезапуск приложения.

        Панель узкая и без адресной строки: встроенный раздел ведёт на
        закреплённый администратором адрес, и предлагать здесь ввод чужого
        означало бы предлагать уйти с него.
      */}
      {!browsable && (
        /*
          Панель встроенного раздела — открывается кнопкой.

          ── Почему не над страницей ─────────────────────────────────────────

          Нативная вкладка лежит ПОВЕРХ страницы приложения, а не внутри неё.
          Отсюда ограничение, которое обойти нельзя: HTML-панель не может
          всплыть НАД содержимым вкладки — вкладка закроет её собой. Всё, что
          рисует приложение, живёт строго за пределами области вкладки.

          ── Почему кнопкой, а не по наведению ───────────────────────────────

          Сначала панель разворачивалась сама при наведении курсора. Так она
          раскрывалась и тогда, когда её не звали: любое движение мыши к
          верхнему краю страницы сдвигало содержимое вкладки вниз. Управление,
          срабатывающее без спроса, раздражает ровно там, где работают весь
          день.

          Теперь состоянием распоряжается человек, и оно запоминается: панель
          нужна редко, и открывать её заново при каждом переходе между
          разделами пришлось бы вручную.
        */
        <div className="flex-none">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setBarOpen(!barOpen)}
              aria-expanded={barOpen}
              aria-controls="section-toolbar"
              aria-label={barOpen ? "Скрыть панель" : "Показать панель"}
              title={barOpen ? "Скрыть панель" : "Показать панель"}
              className="grid h-4 w-12 place-items-center rounded-full border-transparent bg-border/70 text-muted-foreground transition-colors hover:bg-brand/30 hover:text-brand"
            >
              {/* Стрелка сама по себе смотрит ВНИЗ: закрытая панель обещает
                  раскрыться вниз, открытая — свернуться вверх. */}
              <ChevronIcon
                className={cn(
                  "size-3 transition-transform",
                  barOpen ? "rotate-180" : "rotate-0",
                )}
              />
            </button>
          </div>

          <div
            id="section-toolbar"
            className={cn(
              "overflow-hidden transition-[height] duration-150",
              barOpen ? "h-11" : "h-0",
            )}
          >
            <div className="mt-1 flex items-center gap-2 rounded-2xl border border-border bg-card/85 px-2.5 py-1.5 shadow-md backdrop-blur">
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={!active?.canGoBack}
                onClick={() => bridge?.goBack()}
                aria-label="Назад"
                title="Назад"
                className="flex-none text-muted-foreground"
              >
                <ChevronIcon className="size-4 rotate-90" />
              </Button>

              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => bridge?.reloadTab()}
                aria-label="Обновить страницу"
                title="Обновить"
                className="flex-none text-muted-foreground"
              >
                <RefreshIcon
                  className={cn("size-4", active?.loading && "animate-spin")}
                />
              </Button>

              <span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">
                {active?.loading ? "Загрузка…" : (active?.title ?? section.title)}
              </span>

              {/*
                Возврат к началу раздела. Нужен, когда страница ушла вглубь и
                «назад» больше не помогает: у Laravel после 419 история может
                вести обратно в ту же просроченную страницу.
              */}
              {section.url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => bridge?.navigateTab(section.url as string)}
                  className="flex-none text-muted-foreground"
                >
                  К началу
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/*
        Местозаполнитель. Ничего не рисует: содержимое кладёт оболочка поверх
        этой области. Рамка нужна, чтобы область была видна, пока страница
        грузится, — иначе на секунду возникает провал в раскладке.
      */}
      <div
        ref={holder}
        className={cn(
          "min-h-0 flex-1 overflow-hidden rounded-2xl border border-border bg-card shadow-md",
          active?.loading && "animate-pulse",
        )}
        aria-label={section.title}
      />
    </div>
  );
}
